import {
  makeObservable,
  computed,
  observable,
  action,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
} from "mobx";
import { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import { SyncModelKind } from "@/store/sync/types";
import { WithAppStore } from "@/store/types";
import { SourceModelData } from "@/store/sources/types";
import { SyncUpdateValue } from "@/store/sync/types";
import { Uuid } from "@/domains/global/identifiers";
import { liveQuery, Subscription } from "dexie";
import { NoteObservable } from "@/store/note/NoteObservable";

export class SourceObservable extends BaseSyncModel<SourceModelData> {
  public modelKind = SyncModelKind.Source;
  private liveQuerySubscription?: Subscription;
  protected subscribedNoteId?: Uuid;

  constructor({
    id,
    data,
    store,
  }: { id: string; data: SyncUpdateValue<SourceModelData> } & WithAppStore) {
    super({ id, data, store });

    makeObservable<this, "liveQuerySubscription" | "subscribedNoteId">(this, {
      modelKind: false,
      sourceKind: computed,
      primaryLabel: computed,
      secondaryLabel: computed,
      associatedArtifactId: computed,
      associatedNote: computed,
      liveQuerySubscription: false,
      subscribedNoteId: observable,
      subscribeLiveQuery: action,
      unsubscribeLiveQuery: action,
    });

    onBecomeObserved(this, "associatedNote", () => this.subscribeLiveQuery());
    onBecomeUnobserved(this, "associatedNote", () => this.unsubscribeLiveQuery());
  }

  get sourceKind() {
    return this.modelData.source_kind;
  }

  get primaryLabel() {
    return this.modelData.primary_label;
  }

  get secondaryLabel() {
    return this.modelData.secondary_label;
  }

  get associatedArtifactId() {
    return this.modelData.associated_artifact_id;
  }

  subscribeLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();
    this.liveQuerySubscription = liveQuery(async () => {
      const matchingNoteSource = await this.store.noteSources.localTable
        .where("source_id")
        .equals(this.id)
        .first();
      return matchingNoteSource?.model_data?.note_id;
    }).subscribe({
      next: noteId => {
        runInAction(() => {
          this.subscribedNoteId = noteId;
        });
      },
    });
  }

  unsubscribeLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();
  }

  get associatedNote(): NoteObservable | undefined {
    if (!this.subscribedNoteId) return undefined;
    return this.store.notes.get(this.subscribedNoteId);
  }
}
